
// Import section - Replace only the useChatInputHandler part
import React, { useState, useEffect, useRef } from 'react';
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { useChatContext } from "@/hooks/useChatContext";
import { useAuth } from '@/context/AuthContext';
import ChatInsuranceOfferForm from "@/components/chat/ChatInsuranceOfferForm";
import ChatInsuranceComparisonForm from "@/components/chat/ChatInsuranceComparisonForm";
import { detectLanguage } from '@/lib/languageUtils';
import { X } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { useCompanySettings } from '@/context/CompanySettingsContext';
import { CompanySettingsModal } from '@/components/CompanySettingsModal';
import { useTemplateChanges } from '@/hooks/useTemplateChanges';
// Fixed import using correct export name
import { useChatInputHandler } from '@/hooks/useChatInputHandler';
import ChatPanelHeader from '@/components/chat/ChatPanelHeader';
import ChatInputArea from '@/components/chat/ChatInputArea';
import ChatMessagesArea from '@/components/chat/ChatMessagesArea';
import GeneratedLinksPanel from '@/components/chat/GeneratedLinksPanel';
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { ExtractedTemplateEvent } from '@/types/events';

interface ChatPanelProps {
  onEmailGenerated: (content: string, isOffer?: boolean, isComparison?: boolean, analysis?: any) => void;
  onStreamingCodeUpdate?: (html: string) => void;
  onStreamingComplete?: (finalHtml: string) => void;
  initialPrompt?: string | null;
  chatId?: string;
  isBuilderPage?: boolean;
  setIsGenerating?: (isGenerating: boolean) => void;
  triggerTemplateGeneration?: (concept: string) => Promise<void>;
}

const ChatPanel: React.FC<ChatPanelProps> = ({
  onEmailGenerated,
  onStreamingCodeUpdate,
  onStreamingComplete,
  initialPrompt,
  chatId,
  isBuilderPage = false,
  setIsGenerating: externalSetIsGenerating,
  triggerTemplateGeneration: externalTriggerTemplateGeneration
}) => {
  const [isGenerating, setIsGeneratingLocal] = useState<boolean>(false);
  const [showInsuranceOfferForm, setShowInsuranceOfferForm] = useState<boolean>(false);
  const [showInsuranceComparisonForm, setShowInsuranceComparisonForm] = useState<boolean>(false);
  const [showVibeSignForm, setShowVibeSignForm] = useState<boolean>(false);
  const [showOptionsMenu, setShowOptionsMenu] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("chat");
  const [generatedLinks, setGeneratedLinks] = useState<Array<{url: string, id: string, type: string, timestamp: string}>>([]);
  const [showCompanySettings, setShowCompanySettings] = useState<boolean>(false);
  
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [isStreamingTemplate, setIsStreamingTemplate] = useState(false);
  const [streamedTemplateHtml, setStreamedTemplateHtml] = useState('');
  const [templateChangeRequiresConfirmation, setTemplateChangeRequiresConfirmation] = useState<boolean>(false);
  const [pendingConcept, setPendingConcept] = useState<string | null>(null);
  const [showTemplateAssistantMessage, setShowTemplateAssistantMessage] = useState(false);
  const [view, setView] = useState<'preview' | 'code'>('preview');
  const [extractedTemplates, setExtractedTemplates] = useState<Map<string, string>>(new Map());
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const [isTemplateGeneratingState, setIsTemplateGeneratingState] = useState<boolean>(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  
  const { user, credits } = useAuth();
  const { companyInfo } = useCompanySettings();
  
  const chatContext = useChatContext(chatId);
  const { 
    messages, 
    loading: chatLoading, 
    loadMessages,
    isStreaming, 
    currentStreamedContent,
    processMessage
  } = chatContext;

  const handleSetIsGenerating = (value: boolean) => {
    setIsGeneratingLocal(value);
    if (externalSetIsGenerating) {
      externalSetIsGenerating(value);
    }
  };

  useEffect(() => {
    console.log("Chat context messages:", messages?.length);
  }, [messages]);

  const {
    templateChangeRequest,
    setTemplateChangeRequest,
    templateChangeDescription,
    setTemplateChangeDescription,
    templateChangeConfirmed,
    setTemplateChangeConfirmed,
    pendingTemplateChanges,
    setPendingTemplateChanges,
    isGenerating: templateChangesIsGenerating,
    describeTemplateChanges,
    confirmAndProcessTemplateChange,
    cancelTemplateChange,
    saveTemplateVersion,
    getTemplateVersions
  } = useTemplateChanges({ onEmailGenerated, processMessage });

  const scrollToBottom = () => {
    if (scrollAreaRef.current) {
      const { scrollHeight, clientHeight } = scrollAreaRef.current;
      scrollAreaRef.current.scrollTop = scrollHeight - clientHeight;
      setIsAtBottom(true);
    }
  };

  const {
    input,
    setInput,
    handleSubmit,
    triggerTemplateGeneration: inputHandlerTriggerTemplateGeneration,
    isSubmitting // Getting boolean value from the hook
  } = useChatInputHandler({
    onEmailGenerated,
    processMessage,
    user,
    credits,
    emailConcept: pendingConcept,
    setIsGenerating: handleSetIsGenerating,
    setTemplateChangeRequiresConfirmation,
    scrollToBottom,
    setEmailConcept: setPendingConcept
  });

  const effectiveTriggerTemplateGeneration = async (concept: string): Promise<void> => {
    console.log("[DEBUG] Using trigger template generation, external handler:", !!externalTriggerTemplateGeneration);
    
    if (externalTriggerTemplateGeneration) {
      try {
        await externalTriggerTemplateGeneration(concept);
        return;
      } catch (error) {
        console.error("[ERROR] External template generation failed:", error);
      }
    }
    
    try {
      await inputHandlerTriggerTemplateGeneration(concept);
      return;
    } catch (error) {
      console.error("[ERROR] All template generation methods failed:", error);
      toast({
        title: "Fehler bei der Template-Generierung",
        description: "Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.",
        variant: "destructive"
      });
    }
  };

  const handleScroll = () => {
    if (!scrollAreaRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = scrollAreaRef.current;
    const isBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 10;
    setIsAtBottom(isBottom);
  };

  useEffect(() => {
    const scrollArea = scrollAreaRef.current;
    if (scrollArea) {
      scrollArea.addEventListener('scroll', handleScroll);
      return () => scrollArea.removeEventListener('scroll', handleScroll);
    }
  }, []);

  useEffect(() => {
    if (initialPrompt && !chatLoading) {
      if (messages.length === 0 || (messages.length === 1 && messages[0].role === 'assistant')) {
        console.log("Setting initial prompt:", initialPrompt.substring(0, 30));
        setInput(initialPrompt);
        
        const timer = setTimeout(() => {
          handleSubmit();
          localStorage.removeItem('currentPrompt');
          localStorage.removeItem('isGenerating');
        }, 500);
        
        return () => clearTimeout(timer);
      } else {
        localStorage.removeItem('currentPrompt');
        localStorage.removeItem('isGenerating');
      }
    }
  }, [initialPrompt, chatLoading, messages, handleSubmit]);

  useEffect(() => {
    if (chatId && !chatLoading) {
      console.log("ChatPanel: Loading messages for chat ID:", chatId);
      loadMessages();
    }
  }, [chatId, chatLoading, loadMessages]);

  useEffect(() => {
    try {
      const storedLinks = localStorage.getItem('generatedLinks');
      if (storedLinks) {
        setGeneratedLinks(JSON.parse(storedLinks));
      }
    } catch (error) {
      console.error('Error loading links from localStorage:', error);
    }
  }, []);

  useEffect(() => {
    const handleSuggestTemplate = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail?.offerUrl && customEvent.detail?.offerTitle) {
        setInput(`Erstelle eine professionelle E-Mail-Vorlage für mein Versicherungsangebot mit dem Titel "${customEvent.detail.offerTitle}". Die E-Mail sollte einen Button enthalten, der zum Angebot führt: ${customEvent.detail.offerUrl}`);
        setTimeout(() => {
          handleSubmit();
        }, 100);
      }
    };
    
    window.addEventListener('suggest-email-template', handleSuggestTemplate as any);
    
    return () => {
      window.removeEventListener('suggest-email-template', handleSuggestTemplate as any);
    };
  }, [handleSubmit]);

  useEffect(() => {
    const handleRealtimeEvent = (event: CustomEvent) => {
      if (!event.detail) return;
      
      const { type, toolCalls } = event.detail;
      
      console.log("[DEBUG] Realtime event:", type, toolCalls?.length || 0);
      
      if (type === 'tool_calls' && toolCalls?.length > 0) {
        const templateCall = toolCalls.find((call: any) => call.function?.name === 'generate_template');
        if (templateCall && templateCall.function?.arguments) {
          try {
            const args = JSON.parse(templateCall.function.arguments);
            if (args.html) {
              setIsStreamingTemplate(true);
              setStreamedTemplateHtml(args.html);
              if (onStreamingCodeUpdate) {
                console.log("Streaming template update, length:", args.html.length);
                onStreamingCodeUpdate(args.html);
              }
              
              window.dispatchEvent(new CustomEvent('template-content-update', { 
                detail: { content: args.html, isPartial: true }
              }));
            }
          } catch (e) {
            console.error("Error parsing template arguments:", e);
          }
        }
      }
      
      if (type === 'tool_calls_done' && isStreamingTemplate) {
        console.log("[DEBUG] Tool calls done, streaming template complete");
        if (streamedTemplateHtml && onStreamingComplete) {
          console.log("Template streaming complete, length:", streamedTemplateHtml.length);
          onStreamingComplete(streamedTemplateHtml);
          
          window.dispatchEvent(new CustomEvent('template-generation-complete', { 
            detail: { 
              content: streamedTemplateHtml,
              isStreamingComplete: true
            }
          }));
          
          setTimeout(() => {
            setIsStreamingTemplate(false);
            window.dispatchEvent(new CustomEvent('show-template-preview'));
          }, 1000);
        }
      }
    };
    
    window.addEventListener('realtime-event', handleRealtimeEvent as any);
    return () => {
      window.removeEventListener('realtime-event', handleRealtimeEvent as any);
    };
  }, [onStreamingCodeUpdate, onStreamingComplete, isStreamingTemplate, streamedTemplateHtml]);

  useEffect(() => {
    const handleTemplateGenerationStarted = () => {
      console.log("[DEBUG] Template generation started event received");
      handleSetIsGenerating(true);
    };
    
    const handleTemplateGenerationComplete = (e: Event) => {
      const customEvent = e as CustomEvent;
      console.log("[DEBUG] Template generation complete event received");
      handleSetIsGenerating(false);
      
      if (customEvent.detail?.content) {
        if (!customEvent.detail?.isStreamingComplete) {
          onEmailGenerated(customEvent.detail.content);
        }
      }
    };
    
    const handleTemplateContentUpdate = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail?.content && onStreamingCodeUpdate) {
        console.log("[DEBUG] Template content update received, length:", customEvent.detail.content.length);
        onStreamingCodeUpdate(customEvent.detail.content);
      }
    };
    
    const handleTemplateGenerationFailed = (e: Event) => {
      const customEvent = e as CustomEvent;
      console.error("[ERROR] Template generation failed:", customEvent.detail?.error);
      handleSetIsGenerating(false);
      
      toast({
        title: "Fehler bei der Template-Generierung",
        description: customEvent.detail?.error || "Bitte versuchen Sie es erneut",
        variant: "destructive"
      });
    };
    
    window.addEventListener('template-generation-started', handleTemplateGenerationStarted);
    window.addEventListener('template-generation-complete', handleTemplateGenerationComplete as any);
    window.addEventListener('template-content-update', handleTemplateContentUpdate as any);
    window.addEventListener('template-generation-failed', handleTemplateGenerationFailed as any);
    
    return () => {
      window.removeEventListener('template-generation-started', handleTemplateGenerationStarted);
      window.removeEventListener('template-generation-complete', handleTemplateGenerationComplete as any);
      window.removeEventListener('template-content-update', handleTemplateContentUpdate as any);
      window.removeEventListener('template-generation-failed', handleTemplateGenerationFailed as any);
    };
  }, [onEmailGenerated, onStreamingCodeUpdate, handleSetIsGenerating, toast]);

  useEffect(() => {
    const handleShowCodeView = () => {
      setView('code');
    };
    
    const handleShowTemplatePreview = () => {
      setView('preview');
      if (isMobile) {
        const showPreviewPanel = () => {
          const event = new CustomEvent('show-preview-panel');
          window.dispatchEvent(event);
        };
        showPreviewPanel();
      }
    };
    
    window.addEventListener('show-code-view', handleShowCodeView);
    window.addEventListener('show-template-preview', handleShowTemplatePreview);
    
    return () => {
      window.removeEventListener('show-code-view', handleShowCodeView);
      window.removeEventListener('show-template-preview', handleShowTemplatePreview);
    };
  }, [isMobile]);

  useEffect(() => {
    if (!isStreamingTemplate && streamedTemplateHtml) {
      setTimeout(() => {
        setShowTemplateAssistantMessage(true);
      }, 1500);
    }
  }, [isStreamingTemplate, streamedTemplateHtml]);

  const handleConceptConfirmed = (concept: string) => {
    console.log("[DEBUG] Concept confirmed, triggering template generation:", concept.substring(0, 30));
    setPendingConcept(concept);
    setShowTemplateAssistantMessage(false);
    effectiveTriggerTemplateGeneration(concept);
  };

  const handleInsuranceOfferSubmit = (content: string) => {
    onEmailGenerated(content, true);
    setShowInsuranceOfferForm(false);
  };

  const handleInsuranceComparisonSubmit = (content: string, analysis: any) => {
    onEmailGenerated(content, false, true, analysis);
    setShowInsuranceComparisonForm(false);
  };

  const handleLinkGenerated = (linkData: {url: string, id: string, type: string, timestamp: string}) => {
    setGeneratedLinks(prev => [linkData, ...prev]);
    
    try {
      const storedLinks = localStorage.getItem('generatedLinks');
      const links = storedLinks ? JSON.parse(storedLinks) : [];
      links.unshift(linkData);
      localStorage.setItem('generatedLinks', JSON.stringify(links.slice(0, 20)));
    } catch (error) {
      console.error('Error storing link in localStorage:', error);
    }
  };

  useEffect(() => {
    const handleTemplateChangeConfirm = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.messageId && customEvent.detail?.content) {
        setPendingTemplateChanges({
          messageId: customEvent.detail.messageId,
          content: customEvent.detail.content
        });
        
        setTemplateChangeRequest(customEvent.detail.content);
        setTemplateChangeConfirmed(false);
        
        describeTemplateChanges(customEvent.detail.content);
      }
    };
    
    window.addEventListener('confirm-template-changes', handleTemplateChangeConfirm);
    
    return () => {
      window.removeEventListener('confirm-template-changes', handleTemplateChangeConfirm);
    };
  }, [describeTemplateChanges, setPendingTemplateChanges, setTemplateChangeConfirmed, setTemplateChangeRequest]);

  const applyTemplateChanges = async (changeRequest: string) => {
    try {
      handleSetIsGenerating(true);
      scrollToBottom();
      
      const aiMessage = await processMessage(
        changeRequest,
        'email',
        {
          language: detectLanguage(changeRequest) || 'de',
          format: 'html',
          contextType: 'email',
          emailStage: 'template'
        }
      );
      
      if (aiMessage && aiMessage.isEmail) {
        console.log("Applying template changes to preview");
        onEmailGenerated(aiMessage.content);
        
        if (saveTemplateVersion) {
          await saveTemplateVersion(aiMessage.content);
        }
        
        toast({
          title: "Changes applied",
          description: "Your template changes have been applied successfully",
          variant: "default"
        });
      } else {
        console.log("No valid email template in AI response");
        toast({
          title: "Changes not applied",
          description: "Template could not be updated",
          variant: "destructive"
        });
      }
      
      window.dispatchEvent(new CustomEvent('refresh-chat-list'));
    } catch (error) {
      console.error('Failed to update template:', error);
      toast({
        title: "Error",
        description: "Failed to apply template changes",
        variant: "destructive"
      });
    } finally {
      handleSetIsGenerating(false);
      setPendingTemplateChanges(null);
      setTemplateChangeRequiresConfirmation(false);
    }
  };

  const handleConfirmTemplateChange = (messageId: string, content: string) => {
    console.log("Confirming template change:", content);
    setPendingTemplateChanges({messageId, content});
    setTemplateChangeConfirmed(true);
    applyTemplateChanges(content);
  };

  useEffect(() => {
    const handleExtractedTemplate = (event: Event) => {
      const customEvent = event as CustomEvent<ExtractedTemplateEvent>;
      if (customEvent.detail?.content && customEvent.detail?.messageId) {
        setExtractedTemplates(prev => new Map(prev).set(
          customEvent.detail.messageId, 
          customEvent.detail.content
        ));
        
        onEmailGenerated(customEvent.detail.content);
      }
    };
    
    window.addEventListener('open-extracted-template', handleExtractedTemplate as any);
    
    return () => {
      window.removeEventListener('open-extracted-template', handleExtractedTemplate as any);
    };
  }, [onEmailGenerated]);

  useEffect(() => {
    if (messages && messages.length > 0) {
      messages.forEach(msg => {
        if (msg.metadata?.contextType === 'email') {
          console.log(`Message ${msg.id.substring(0, 8)}: contextType=${msg.metadata?.contextType}, emailStage=${msg.metadata?.emailStage}`);
        }
      });
    }
  }, [messages]);
  
  useEffect(() => {
    if (chatId) {
      console.log("Forcing message reload on component mount");
      loadMessages();
    }
  }, [loadMessages, chatId]);

  const handleRequestIdeas = (context: string) => {
    processMessage(
      `Bitte gib mir kreative E-Mail-Ideen zum Kontext: ${context}`,
      'email',
      {
        language: detectLanguage(context) || 'de',
        contextType: 'email',
        emailStage: 'ideas',
        isNewEmailRequest: true,
        role: 'assistant'
      }
    );
  };

  const renderChatContent = () => {
    if (showInsuranceOfferForm) {
      return (
        <div className="flex-1 overflow-y-auto p-4">
          <ChatInsuranceOfferForm
            onEmailGenerated={handleInsuranceOfferSubmit}
            onClose={() => setShowInsuranceOfferForm(false)}
            onLinkGenerated={handleLinkGenerated}
          />
        </div>
      );
    }
    if (showInsuranceComparisonForm) {
      return (
        <div className="flex-1 overflow-y-auto p-4">
          <ChatInsuranceComparisonForm
            onAnalysisComplete={(content) => handleInsuranceComparisonSubmit(content, null)}
            onClose={() => setShowInsuranceComparisonForm(false)}
            onLinkGenerated={handleLinkGenerated}
          />
        </div>
      );
    }
    if (showVibeSignForm) {
      return (
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold text-white">VibeSign</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowVibeSignForm(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-gray-300">
              Dokumente digital signieren und verwalten.
            </p>
            <Button
              variant="default"
              onClick={() => {
                setShowVibeSignForm(false);
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><path d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Z"/><path d="m8 12 3 3 6-6"/></svg>
              Dokument hochladen
            </Button>
          </div>
        </div>
      );
    }
    return (
      <>
        <ChatMessagesArea
          messages={messages}
          isStreaming={isStreaming}
          triggerTemplateGeneration={effectiveTriggerTemplateGeneration}
          templateChangeDescription={templateChangeDescription}
          templateChangeRequest={templateChangeRequest}
          templateChangeConfirmed={templateChangeConfirmed}
          isTemplateGenerating={isTemplateGeneratingState}
          confirmAndProcessTemplateChange={confirmAndProcessTemplateChange}
          cancelTemplateChange={cancelTemplateChange}
          onEmailGenerated={onEmailGenerated}
          onConceptConfirmed={handleConceptConfirmed}
          templateChangeRequiresConfirmation={templateChangeRequiresConfirmation}
          handleConfirmTemplateChange={handleConfirmTemplateChange}
          extractedTemplates={extractedTemplates}
          isAtBottom={isAtBottom}
          scrollToBottom={scrollToBottom}
          onOpenOfferForm={() => setShowInsuranceOfferForm(true)}
          onOpenComparisonForm={() => setShowInsuranceComparisonForm(true)}
          onRequestIdeas={handleRequestIdeas}
        />
        {showTemplateAssistantMessage && (
          <div className="flex items-start mt-4 animate-fade-in max-w-[90%]">
            <div className="mr-auto bg-gradient-to-br from-gray-700 via-gray-800 to-gray-900 text-white shadow-xl border border-jade/50 p-4 rounded-lg">
              <span className="font-bold text-jade block mb-1">✓ E-Mail-Template erstellt!</span>
              <div className="text-sm">
                Das HTML-Template wurde erfolgreich generiert und ist nun als Vorschau sichtbar.<br />
                <span className="block my-1">Sie können jetzt weitere Änderungen vornehmen – formulieren Sie zum Beispiel:</span>
                <ul className="list-disc list-inside ml-2 mt-1 mb-2 text-jade/80">
                  <li>"Füge ein Logo hinzu"</li>
                  <li>"Passe die Farben an das Firmenbranding an"</li>
                  <li>"Mache den Text kürzer"</li>
                </ul>
                <span>Oder teilen Sie uns mit, wie wir Ihr Template noch weiter optimieren können!</span>
              </div>
            </div>
          </div>
        )}
        <ChatInputArea
          input={input}
          setInput={setInput}
          handleSubmit={handleSubmit}
          isSubmitting={isSubmitting} // Pass boolean directly instead of a ref
          isGenerating={isGenerating}
          isStreaming={isStreaming}
          emailConcept={pendingConcept}
          companyInfo={companyInfo}
          setShowCompanySettings={setShowCompanySettings}
          textareaRef={textareaRef}
        />
      </>
    );
  };

  return (
    <div className="flex flex-col h-full bg-black overflow-hidden">
      {showCompanySettings && (
        <CompanySettingsModal 
          open={showCompanySettings}
          onOpenChange={setShowCompanySettings}
        />
      )}
      
      <Tabs 
        defaultValue="chat" 
        value={activeTab} 
        onValueChange={setActiveTab}
        className="flex flex-col h-full"
      >
        <ChatPanelHeader
          activeTab={activeTab}
          setShowCompanySettings={setShowCompanySettings}
          setShowOptionsMenu={setShowOptionsMenu}
          showOptionsMenu={showOptionsMenu}
          setShowInsuranceOfferForm={setShowInsuranceOfferForm}
          setShowInsuranceComparisonForm={setShowInsuranceComparisonForm}
          setShowVibeSignForm={setShowVibeSignForm}
          setInput={setInput}
          textareaRef={textareaRef}
          credits={credits}
          isBuilderPage={isBuilderPage}
        />
        
        <TabsContent value="chat" className="flex-1 overflow-hidden flex flex-col p-0 m-0 data-[state=inactive]:hidden">
          {renderChatContent()}
        </TabsContent>
        
        <TabsContent value="links" className="flex-1 overflow-hidden flex flex-col p-0 m-0 data-[state=inactive]:hidden">
          <GeneratedLinksPanel generatedLinks={generatedLinks} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ChatPanel;
